import { useEffect, useState } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { hasToken, useSessionReady } from '../../hooks/useSessionReady';
import { FTL_NOTES_CHANGED_EVENT, fetchOpenFtlNotes } from './ftlNotesApi';

/**
 * Pozycja „Uwagi od FTL” w menu z licznikiem otwartych uwag (makieta back
 * office v2, menu CS: „Uwagi od FTL” z liczbą niezałatwionych; 20.10.2026).
 *
 * Menu pokazuje pozycję tylko z przełącznikiem `ftlNotes` i uprawnieniem
 * „Przyjęcie zleceń” (`menuConfig.ts`), więc licznik pyta API tylko wtedy.
 * Zero i błąd odczytu — sama etykieta (pusty licznik byłby szumem, jak przy
 * liczniku bezczynnych). Odświeża się co minutę i od razu po zmianie uwag
 * w tej karcie przeglądarki (`FTL_NOTES_CHANGED_EVENT`).
 */

export const FTL_NOTES_REFRESH_MS = 60_000;

export const FtlNotesMenuLabel = ({ label }: { label: string }) => {
  const ready = useSessionReady();
  const [count, setCount] = useState<{ open: number; urgent: number } | null>(null);

  useEffect(() => {
    if (!ready) {
      setCount(null);
      return undefined;
    }
    let active = true;
    const load = () => {
      if (!hasToken()) return;
      fetchOpenFtlNotes()
        .then((c) => {
          if (active) setCount(typeof c?.open === 'number' ? c : null);
        })
        .catch(() => {
          if (active) setCount(null);
        });
    };
    load();
    const timer = setInterval(load, FTL_NOTES_REFRESH_MS);
    window.addEventListener(FTL_NOTES_CHANGED_EVENT, load);
    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener(FTL_NOTES_CHANGED_EVENT, load);
    };
  }, [ready]);

  const open = count?.open ?? 0;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      <span>{label}</span>
      {open > 0 ? (
        <Tooltip
          title={`Otwarte uwagi: ${open}${count && count.urgent > 0 ? `, w tym pilne: ${count.urgent}` : ''}`}
        >
          <Chip
            size="small"
            color={count && count.urgent > 0 ? 'error' : 'warning'}
            label={open}
            data-testid="ftl-notes-count"
            sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
          />
        </Tooltip>
      ) : null}
    </Box>
  );
};
